import { ApiResult, SearchDTO, SearchVM } from 'common/models';
import { HttpClient } from 'common/services/http-client.service';
import { EntityType } from 'common/enums';
import { IndicatorAddDTO, IndicatorUpdateDTO } from 'common/models/data/indicator.model';
import { Schema } from 'common/models/schema/schema.model';
import fileUploadConfig from 'common/redux/file-upload/fileUpload.config';

import { checkIfCategoryHasSubCategoryOrIndicator } from 'common/utils/table';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { PaginatedApiResponse } from 'common/models/api-service/api-response.model';
import { CategoryDTO, Entity, EntityVM } from 'common/models/data/entity.model';
import { IErrorApiResponse } from 'common/models/api-service/error-response.model';

import {
    EditSubCategoryDTO,
    LinkIndicatorDTO,
    SubCategoryDTO
} from 'modules/master-data/models/master-data.model';
import { NestCreationTypes } from 'modules/master-data/enums/mode.enum';

import masterDataApiConfig from './masterData.config';
import { ExportRequestDto } from '../models/export.model';

export class MasterDataService extends HttpClient {
    public static async getCategories(
        pagedRequest: PagedRequest
    ): Promise<ApiResult<PaginatedApiResponse<Entity[]> | IErrorApiResponse> | undefined> {
        return MasterDataService.invokeApi<PaginatedApiResponse<Entity[]> | IErrorApiResponse>(
            masterDataApiConfig.getCategoriesUrl(pagedRequest)
        );
    }

    public static async getEntitiesByCategoryId(
        categoryId: string
    ): Promise<ApiResult<EntityVM[] | IErrorApiResponse> | undefined> {
        return MasterDataService.invokeApi<EntityVM[] | IErrorApiResponse>(
            masterDataApiConfig.getEntitiesByCategoryIdUrl(categoryId)
        );
    }

    public static async getEntitiesByHierarchyId(
        hierarchyId: string
    ): Promise<ApiResult<EntityVM[] | IErrorApiResponse> | undefined> {
        return MasterDataService.invokeApi<EntityVM[] | IErrorApiResponse>(
            masterDataApiConfig.getEntitiesByHierarchyIdUrl(hierarchyId)
        );
    }

    public static updateCategory(data: CategoryDTO) {
        return MasterDataService.invokeApi<CategoryDTO | IErrorApiResponse>(
            masterDataApiConfig.editCategoryUrl(data)
        );
    }

    public static async addSubCategory(
        data: SubCategoryDTO
    ): Promise<ApiResult<SubCategoryDTO | IErrorApiResponse> | undefined> {
        return MasterDataService.invokeApi<SubCategoryDTO | IErrorApiResponse>(
            masterDataApiConfig.addSubCategoryUrl(data)
        );
    }

    public static async updateSubCategory(data: EditSubCategoryDTO) {
        return MasterDataService.invokeApi<EditSubCategoryDTO | IErrorApiResponse>(
            masterDataApiConfig.updateSubCategoryUrl(data)
        );
    }

    public static async fetchSubCategory(
        subCategoryId: string
    ): Promise<ApiResult<SubCategoryDTO | IErrorApiResponse> | undefined> {
        return MasterDataService.invokeApi<SubCategoryDTO | IErrorApiResponse>(
            masterDataApiConfig.fetchSubCategoryUrl(subCategoryId)
        );
    }

    public static updateIndicator(data: IndicatorUpdateDTO) {
        return MasterDataService.invokeApi<IndicatorUpdateDTO | IErrorApiResponse>(
            masterDataApiConfig.updateIndicatorUrl(data)
        );
    }

    public static deleteData(id: string, entityType: EntityType, hierarchyId: string) {
        switch (entityType) {
            case EntityType.Category:
                return MasterDataService.invokeApi<any>(masterDataApiConfig.deleteCategoryUrl(id));
            case EntityType.SubCategory:
                return MasterDataService.invokeApi<any>(
                    masterDataApiConfig.deleteSubCategoryUrl(id)
                );
            case EntityType.Indicator:
                return MasterDataService.invokeApi<any>(
                    masterDataApiConfig.deleteIndicatorUrl(id, hierarchyId)
                );
        }
    }

    public static async getSearchList(
        data: SearchDTO
    ): Promise<ApiResult<PaginatedApiResponse<SearchVM[]>> | undefined> {
        return MasterDataService.invokeApi<PaginatedApiResponse<SearchVM[]>>(
            masterDataApiConfig.getSearchUrl(data)
        );
    }

    public static addCategory(payload: CategoryDTO) {
        return MasterDataService.invokeApi<CategoryDTO>(
            masterDataApiConfig.addCategoryUrl(payload)
        );
    }

    public static saveToMaster() {
        return {
            hasErrors: false,

            errors: []
        };
    }

    public static applyChanges(payload: any) {
        const err = checkIfCategoryHasSubCategoryOrIndicator(payload);
        if (err.length === 0) {
            return {
                hasErrors: false,

                errors: []
            };
        } else {
            return {
                hasErrors: true,

                errors: err
            };
        }
    }

    public static addIndicator(data: any) {
        if (data.createNewEntityType == NestCreationTypes.CATEGORY) {
            return MasterDataService.invokeApi<any | IErrorApiResponse>(
                masterDataApiConfig.addIndicatorUnderNewCategoryUrl(data)
            );
        } else if (data.createNewEntityType == NestCreationTypes.SUB_CATEGORY) {
            return MasterDataService.invokeApi<any | IErrorApiResponse>(
                masterDataApiConfig.addIndicatorUnderNewSubCategoryUrl(data)
            );
        } else {
            return MasterDataService.invokeApi<any | IErrorApiResponse>(
                masterDataApiConfig.addIndicatorUrl(data)
            );
        }
    }

    public static linkIndicator(data: LinkIndicatorDTO) {
        return MasterDataService.invokeApi(masterDataApiConfig.linkIndicatorUrl(data));
    }

    public static archive(id: string, entityType: EntityType, hierarchyId: string) {
        switch (entityType) {
            case EntityType.Category:
                return MasterDataService.invokeApi<number>(
                    masterDataApiConfig.archiveCategoryUrl(id)
                );
            case EntityType.SubCategory:
                return MasterDataService.invokeApi<number>(
                    masterDataApiConfig.archiveSubCategoryUrl(id)
                );
            case EntityType.Indicator:
                return MasterDataService.invokeApi<number>(
                    masterDataApiConfig.archiveIndicatorUrl(id, hierarchyId)
                );
        }
    }

    public static getEntityDetails(payload: any) {
        return {
            hasError: false,
            errors: [],
            value: {
                name: payload.name,
                dateAdded: '2023-06-02T10:37:32.7766667',
                editedUser: 'pl_Core_to_Temp_latest',
                lastModified: '2023-06-02T10:37:32.7766667',
                mappedto: null
            }
        };
    }

    public static cloneEntity(payload: any) {
        return MasterDataService.invokeApi<boolean | IErrorApiResponse>(
            masterDataApiConfig.cloneEntity(payload)
        );
    }

    public static async getReviewStatusUrl(): Promise<
        ApiResult<boolean | IErrorApiResponse> | undefined
    > {
        return MasterDataService.invokeApi<boolean | IErrorApiResponse>(
            masterDataApiConfig.getReviewStatusUrl()
        );
    }

    public static async moveSubCategory(
        payload: any
    ): Promise<ApiResult<boolean | IErrorApiResponse> | undefined> {
        if (payload.createNewEntityType == 'createNewCategory') {
            return MasterDataService.invokeApi<boolean | IErrorApiResponse>(
                masterDataApiConfig.moveSubcategoryUnderNewCategory(payload)
            );
        } else if (payload.createNewEntityType == 'createNewSubCategory') {
            return MasterDataService.invokeApi<boolean | IErrorApiResponse>(
                masterDataApiConfig.moveSubcategoryUnderNewSubcategory(payload)
            );
        } else {
            return MasterDataService.invokeApi<boolean | IErrorApiResponse>(
                masterDataApiConfig.moveSubCategory(payload)
            );
        }
    }

    public static async moveIndicator(
        payload: any
    ): Promise<ApiResult<boolean | IErrorApiResponse> | undefined> {
        if (payload.createNewEntityType == 'createNewCategory') {
            return MasterDataService.invokeApi<boolean | IErrorApiResponse>(
                masterDataApiConfig.moveIndicatorUnderNewCategory(payload)
            );
        } else if (payload.createNewEntityType == 'createNewSubCategory') {
            return MasterDataService.invokeApi<boolean | IErrorApiResponse>(
                masterDataApiConfig.moveIndicatorUnderNewSubcategory(payload)
            );
        } else {
            return MasterDataService.invokeApi<boolean | IErrorApiResponse>(
                masterDataApiConfig.moveIndicator(payload)
            );
        }
    }

    public static loadToReviewSchema(payload: Schema) {
        return this.invokeApi(fileUploadConfig.addSyncLog(payload));
    }

    public static getDeleteFeasibility(payload: any) {
        return this.invokeApi(masterDataApiConfig.getDeleteFeasibility(payload));
    }

    public static getArchiveFeasibility(payload: {
        id: string;
        hierarchyId: string;
        entityType: EntityType;
    }) {
        return this.invokeApi(masterDataApiConfig.getArchiveFeasibility(payload));
    }

    public static getIsMovableSubCategory(payload: any) {
        return this.invokeApi(masterDataApiConfig.getIsMovableSubCategory(payload));
    }

    public static discardChanges() {
        return MasterDataService.invokeApi(masterDataApiConfig.discardChangesUrl());
    }

    public static getCloneFeasibility(payload: { id: string; entityType: EntityType }) {
        return this.invokeApi(masterDataApiConfig.getCloneFeasibility(payload));
    }

    public static checkDuplicateIndicator(payload: string) {
        return this.invokeApi(masterDataApiConfig.checkDuplicateIndicator(payload));
    }

    public static async exportData(payload: ExportRequestDto): Promise<ApiResult<any> | undefined> {
        return this.invokeApi(masterDataApiConfig.exportData(payload));
    }
}
